import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Signed-in feedback modal — reuses POST /api/contact so admins see it on
// the same Contacts tab as public landing-page submissions. Pre-fills
// name/email from the logged-in user; the diagnostics checkbox appends
// build/viewport/user-agent lines to the message body so triage doesn't
// need a follow-up email.
export default function FeedbackModal({ open, onOpenChange, source = "app" }) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Please write a message before sending.");
      return;
    }
    setSending(true);
    try {
      let body = trimmed;
      if (includeDiagnostics) {
        const build = process.env.REACT_APP_BUILD_DATE
          ? `v${process.env.REACT_APP_BUILD_DATE}${process.env.REACT_APP_BUILD_ID ? " · " + process.env.REACT_APP_BUILD_ID : ""}`
          : "unknown";
        const diag = [
          "",
          "— App details —",
          `Source: ${source}`,
          `Build: ${build}`,
          `Path: ${window.location.pathname}`,
          `Viewport: ${window.innerWidth}×${window.innerHeight}`,
          `User agent: ${navigator.userAgent}`,
        ].join("\n");
        body = trimmed + diag;
      }
      await api.post("/contact", {
        name: user?.name || "Signed-in user",
        email: user?.email || "unknown@yourdogdays.com",
        message: body,
      });
      toast.success("Thanks! Your feedback landed in the admin inbox.");
      setMessage("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — try again in a bit.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Bug, feature request, or a nice note — all of it reaches Karin directly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-xs text-muted-foreground">
            Sending as <strong>{user?.name || "you"}</strong> · {user?.email}
          </div>
          <div>
            <Label htmlFor="feedback-message" className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Message
            </Label>
            <Textarea
              id="feedback-message"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind? Screenshots welcome via email reply once we're in touch."
              data-testid="feedback-message"
              maxLength={4500}
            />
            <p className="mt-1 text-[11px] text-muted-foreground text-right">
              {message.length} / 4500
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeDiagnostics}
              onChange={(e) => setIncludeDiagnostics(e.target.checked)}
              className="h-4 w-4"
              data-testid="feedback-include-diagnostics"
            />
            <span>Include app details (build, page, browser) — helps with triage</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="feedback-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={sending} data-testid="feedback-submit">
              {sending ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
